import { useEffect, useCallback } from 'react';

export interface KeyboardShortcutsOptions {
  onPlayPause: () => void;
  onSeekForward: () => void;
  onSeekBackward: () => void;
  onSpeedUp: () => void;
  onSpeedDown: () => void;
  onPrevTrack?: () => void;
  onNextTrack?: () => void;
  onFocusSearch?: () => void;
  onCloseModal?: () => void;
  enabled?: boolean;
}

/**
 * Keyboard shortcuts for audio player
 *
 * Shortcuts:
 * - Space / K: Play/Pause
 * - J / Left Arrow: Rewind 30s
 * - L / Right Arrow: Forward 30s
 * - . (period): Increase speed
 * - , (comma): Decrease speed
 * - /: Focus search box
 * - Escape: Close modal
 * - Media Track Previous: Previous track
 * - Media Track Next: Next track
 */
export function useKeyboardShortcuts(options: KeyboardShortcutsOptions): void {
  const {
    onPlayPause,
    onSeekForward,
    onSeekBackward,
    onSpeedUp,
    onSpeedDown,
    onPrevTrack,
    onNextTrack,
    onFocusSearch,
    onCloseModal,
    enabled = true,
  } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in an input
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Allow Escape even when typing
      if (event.key === 'Escape' && onCloseModal) {
        onCloseModal();
        return;
      }

      // Skip other shortcuts when typing
      if (isTyping) return;

      switch (event.key) {
        case ' ':
        case 'k':
        case 'K':
          event.preventDefault();
          onPlayPause();
          break;

        case 'j':
        case 'J':
        case 'ArrowLeft':
          event.preventDefault();
          onSeekBackward();
          break;

        case 'l':
        case 'L':
        case 'ArrowRight':
          event.preventDefault();
          onSeekForward();
          break;

        case '.':
          event.preventDefault();
          onSpeedUp();
          break;

        case ',':
          event.preventDefault();
          onSpeedDown();
          break;

        case '/':
          event.preventDefault();
          if (onFocusSearch) {
            onFocusSearch();
          }
          break;

        case 'MediaTrackPrevious':
          event.preventDefault();
          if (onPrevTrack) {
            onPrevTrack();
          }
          break;

        case 'MediaTrackNext':
          event.preventDefault();
          if (onNextTrack) {
            onNextTrack();
          }
          break;
      }
    },
    [
      enabled,
      onPlayPause,
      onSeekForward,
      onSeekBackward,
      onSpeedUp,
      onSpeedDown,
      onPrevTrack,
      onNextTrack,
      onFocusSearch,
      onCloseModal,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Available keyboard shortcuts for display
 */
export const KEYBOARD_SHORTCUTS = [
  { key: 'Space / K', action: 'Play/Pause' },
  { key: 'J / Left Arrow', action: 'Rewind 30s' },
  { key: 'L / Right Arrow', action: 'Forward 30s' },
  { key: '.', action: 'Increase speed' },
  { key: ',', action: 'Decrease speed' },
  { key: '/', action: 'Focus search' },
  { key: 'Escape', action: 'Close modal' },
  { key: 'Media Prev', action: 'Previous track' },
  { key: 'Media Next', action: 'Next track' },
] as const;
